import { useState } from "react";
import { FileDown, FileX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";

const save = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const useDocDownload = () => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(null);
  const download = async (docType, ref) => {
    setBusy(`${docType}:${ref}`);
    try {
      const r = await api.get("/documents/pdf", { params: { doc_type: docType, ref }, responseType: "blob" });
      const enc = r.headers["x-doc-no"];
      const docNo = enc ? new TextDecoder().decode(Uint8Array.from(atob(enc), (c) => c.charCodeAt(0))) : ref;
      if (String(r.headers["content-type"] || "").startsWith("text/html")) {  // SoftOne print preview: open in a tab, user prints / saves as PDF
        const w = window.open(URL.createObjectURL(r.data), "_blank");
        if (!w) save(r.data, `${docNo}.html`);
        return;
      }
      save(r.data, `${docNo}.pdf`);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? t("docs.notYet") : t("common.error"));
    } finally { setBusy(null); }
  };
  return { busy, download };
};

export const DocButton = ({ docType, refNo, ready, label, testId }) => {
  const { t } = useI18n();
  const { busy, download } = useDocDownload();
  const loading = busy === `${docType}:${refNo}`;
  if (!ready) {
    return (
      <div data-testid={`${testId}-unavailable`} className="flex h-12 flex-1 items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-4 text-sm text-slate-500">
        <FileX className="h-4 w-4 shrink-0" /><span className="truncate">{label} · {t("docs.notYet")}</span>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => download(docType, refNo)} disabled={loading} data-testid={testId}
      className="flex h-12 flex-1 items-center gap-2 rounded-2xl bg-white px-4 text-sm font-semibold text-navy shadow-sm transition-shadow hover:shadow-md disabled:opacity-60">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 text-brand" />}<span className="truncate">{label}</span>
    </button>
  );
};

export const DocIconButton = ({ refNo, testId }) => {
  const { t } = useI18n();
  const { busy, download } = useDocDownload();
  return (
    <button type="button" onClick={() => download("invoice", refNo)} disabled={!!busy} aria-label={t("docs.download")} data-testid={testId}
      className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
    </button>
  );
};
